import { BulkRowError } from "@/types/doctorInvitation.definition";
import {
  BulkStaticDoctorProfileImportRequest,
  StaticDoctorProfileDto,
} from "@/types/staticDoctorProfile.definition";
import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import staticDoctorProfileApi from "../../services/staticDoctorProfile.api";
import {
  StaticDoctorProfileApiError,
  bulkImportStaticDoctorProfilesFailure,
  bulkImportStaticDoctorProfilesRequest,
  bulkImportStaticDoctorProfilesSuccess,
  fetchStaticDoctorProfilesFailure,
  fetchStaticDoctorProfilesRequest,
  fetchStaticDoctorProfilesSuccess,
  inviteFromProfileFailure,
  inviteFromProfileRequest,
  inviteFromProfileSuccess,
} from "../reducers/staticDoctorProfileSlice";

type ApiErrorShape = {
  code?: string;
  message?: string;
  data?: BulkRowError[];
};

function toStaticDoctorProfileApiError(
  error: unknown,
  fallbackMessage: string
): StaticDoctorProfileApiError {
  const e = error as ApiErrorShape;
  if (e && typeof e === "object" && e.code) {
    return { code: e.code, message: e.message, data: e.data };
  }
  return { code: "UNHANDLED_ERROR", message: fallbackMessage };
}

function* fetchStaticDoctorProfilesSaga() {
  try {
    const profiles: StaticDoctorProfileDto[] = yield call(
      staticDoctorProfileApi.getAll
    );
    yield put(fetchStaticDoctorProfilesSuccess(profiles));
  } catch (error) {
    yield put(
      fetchStaticDoctorProfilesFailure(
        toStaticDoctorProfileApiError(
          error,
          "Failed to fetch static doctor profiles"
        )
      )
    );
  }
}

function* bulkImportStaticDoctorProfilesSaga(action: {
  payload: BulkStaticDoctorProfileImportRequest;
}) {
  try {
    const profiles: StaticDoctorProfileDto[] = yield call(
      staticDoctorProfileApi.bulkImport,
      action.payload
    );
    yield put(bulkImportStaticDoctorProfilesSuccess(profiles));
  } catch (error) {
    yield put(
      bulkImportStaticDoctorProfilesFailure(
        toStaticDoctorProfileApiError(
          error,
          "Failed to import static doctor profiles"
        )
      )
    );
  }
}

function* inviteFromProfileSaga(action: { payload: number }) {
  try {
    const profile: StaticDoctorProfileDto = yield call(
      staticDoctorProfileApi.inviteFromProfile,
      action.payload
    );
    yield put(inviteFromProfileSuccess(profile));
  } catch (error) {
    yield put(
      inviteFromProfileFailure(
        toStaticDoctorProfileApiError(error, "Failed to invite doctor")
      )
    );
  }
}

// Root saga
export function* staticDoctorProfileSaga() {
  yield takeLatest(
    fetchStaticDoctorProfilesRequest,
    fetchStaticDoctorProfilesSaga
  );
  yield takeEvery(
    bulkImportStaticDoctorProfilesRequest,
    bulkImportStaticDoctorProfilesSaga
  );
  yield takeEvery(inviteFromProfileRequest, inviteFromProfileSaga);
}
